import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Prototype {
  op_id: string;
  name: string;
  category: string;
  style_taxonomy: string;
  image_url: string;
}

interface PrototypeGalleryProps {
  onSelect: (prototype: Prototype) => void;
  selectedPrototypeId?: string;
}

const API_BASE_URL = 'https://fdaia-api.lancetkeith.workers.dev';

export function PrototypeGallery({ onSelect, selectedPrototypeId }: PrototypeGalleryProps) {
  const [prototypes, setPrototypes] = useState<Prototype[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'women' | 'men'>('all');

  useEffect(() => {
    fetchPrototypes();
  }, []);

  const fetchPrototypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/fdaia/prototypes`);

      if (!response.ok) {
        throw new Error(`Failed to fetch prototypes: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.prototypes) {
        setPrototypes(data.prototypes);
        // Auto-select first prototype if none selected
        if (!selectedPrototypeId && data.prototypes.length > 0) {
          onSelect(data.prototypes[0]);
        }
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      console.error('Error fetching prototypes:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrototypes = prototypes.filter(p =>
    filter === 'all' ? true : p.category === filter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-ck-cyan" />
        <span className="ml-2 text-sm text-muted-foreground">Loading prototypes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
        <p className="text-sm text-red-400">Error loading prototypes: {error}</p>
        <button
          onClick={fetchPrototypes}
          className="mt-3 text-xs text-ck-cyan underline hover:text-ck-cyan/80"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category Filter */}
      <div className="flex gap-2">
        {(['all', 'women', 'men'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
              filter === cat
                ? 'bg-ck-cyan text-black'
                : 'bg-ck-dark-card text-muted-foreground hover:bg-ck-dark-card/70'
            )}
          >
            {cat === 'all' ? 'All Styles' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Prototype Grid */}
      <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2">
        {filteredPrototypes.map((prototype) => (
          <button
            key={prototype.op_id}
            onClick={() => onSelect(prototype)}
            className={cn(
              'group relative overflow-hidden rounded-lg border-2 transition-all hover:scale-105',
              selectedPrototypeId === prototype.op_id
                ? 'border-ck-cyan shadow-lg shadow-ck-cyan/20'
                : 'border-ck-dark-border hover:border-ck-cyan/50'
            )}
          >
            {/* Image */}
            <div className="aspect-square bg-ck-dark-card">
              <img
                src={prototype.image_url}
                alt={prototype.name}
                className="h-full w-full object-cover transition-transform group-hover:scale-110"
                loading="lazy"
              />
            </div>

            {/* Label */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-2">
              <p className="text-xs font-medium text-white line-clamp-2">
                {prototype.name}
              </p>
              <p className="text-[10px] text-ck-cyan uppercase">
                {prototype.style_taxonomy.replace(/_/g, ' ')}
              </p>
            </div>

            {/* Selected Indicator */}
            {selectedPrototypeId === prototype.op_id && (
              <div className="absolute top-2 right-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-ck-cyan">
                  <svg className="h-4 w-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Count */}
      <p className="text-center text-xs text-muted-foreground">
        {filteredPrototypes.length} {filter === 'all' ? 'total' : filter} prototype{filteredPrototypes.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
